/**
 * Citation export utilities: RIS, BibTeX, and plain text.
 * Simplified from widgets for paperclip's paper-only citations.
 */

import type { Citation } from "./citations";

export type ExportFormat = "ris" | "bibtex" | "txt";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const parseYear = (monthYear?: string): string | undefined =>
  monthYear?.match(/(\d{4})/)?.[1];

const parseMonth = (monthYear?: string): string | undefined =>
  monthYear?.match(/^([A-Za-z]+)/)?.[1];

const parseAuthors = (authors?: string): string[] => {
  if (!authors) return [];
  return authors
    .replace(/,?\s*et al\.?$/i, "")
    .split(/;\s*|,\s*(?=[A-Z])/)
    .map((a) => a.trim())
    .filter(Boolean);
};

const sanitizeBibKey = (c: Citation): string => {
  const authorPart =
    parseAuthors(c.authors)[0]
      ?.split(/[,\s]/)[0]
      ?.toLowerCase()
      ?.replace(/[^a-z]/g, "") || "ref";
  const year = parseYear(c.month_year) || "nd";
  const idPart = (c.doc_id || c.block_id || "")
    .slice(0, 8)
    .replace(/[^a-zA-Z0-9]/g, "");
  return `${authorPart}${year}${idPart}`;
};

const buildUrl = (c: Citation): string | undefined =>
  c.public_url || (c.doi ? `https://doi.org/${c.doi}` : undefined);

const journalName = (c: Citation): string | undefined => {
  if (c.source === "pmc" || c.source_type === "pmc_xml") return undefined;
  if (c.source === "biorxiv" || c.source_type === "biorxiv_xml")
    return "bioRxiv";
  if (c.source === "medrxiv" || c.source_type === "medrxiv_xml")
    return "medRxiv";
  return undefined;
};

const risType = (c: Citation): string => {
  if (c.source_type === "patent") return "PAT";
  return "JOUR";
};

// ---------------------------------------------------------------------------
// RIS
// ---------------------------------------------------------------------------

const citationToRIS = (c: Citation): string => {
  const lines: string[] = [];
  const add = (tag: string, value?: string) => {
    if (value) lines.push(`${tag}  - ${value}`);
  };

  add("TY", risType(c));
  for (const author of parseAuthors(c.authors)) add("AU", author);
  add("TI", c.doc_title);

  const journal = journalName(c);
  if (journal) add("JO", journal);

  const year = parseYear(c.month_year);
  if (year) add("PY", year);

  add("DO", c.doi);
  add("UR", buildUrl(c));

  if (c.section) add("N1", `Section: ${c.section}`);
  if (c.content) add("AB", c.content.slice(0, 1000));

  lines.push("ER  - ");
  return lines.join("\r\n");
};

// ---------------------------------------------------------------------------
// BibTeX
// ---------------------------------------------------------------------------

const escapeBibtex = (s: string): string =>
  s.replace(/([&%$#_{}~^\\])/g, "\\$1");

const citationToBibTeX = (c: Citation): string => {
  const key = sanitizeBibKey(c);
  const isPreprint =
    c.source === "biorxiv" ||
    c.source === "medrxiv" ||
    c.source_type === "biorxiv_xml" ||
    c.source_type === "medrxiv_xml";
  const type =
    c.source_type === "patent" ? "misc" : isPreprint ? "article" : "article";

  const fields: string[] = [];
  const add = (field: string, value?: string) => {
    if (value) fields.push(`  ${field} = {${escapeBibtex(value)}}`);
  };

  const authors = parseAuthors(c.authors);
  if (authors.length) add("author", authors.join(" and "));
  add("title", c.doc_title);

  const journal = journalName(c);
  if (journal) add("journal", journal);
  add("year", parseYear(c.month_year));
  add("month", parseMonth(c.month_year));
  add("doi", c.doi);
  add("url", buildUrl(c));
  if (isPreprint) add("note", "Preprint");

  return `@${type}{${key},\n${fields.join(",\n")}\n}`;
};

// ---------------------------------------------------------------------------
// Plain text
// ---------------------------------------------------------------------------

const formatPlainTextRef = (c: Citation): string => {
  const isBioRxiv = c.source === "biorxiv" || c.source_type === "biorxiv_xml";
  const isMedRxiv = c.source === "medrxiv" || c.source_type === "medrxiv_xml";
  const isPmc = c.source === "pmc" || c.source_type === "pmc_xml";

  if (c.doi || isBioRxiv || isMedRxiv || isPmc) {
    const parts: string[] = [];
    if (c.authors) parts.push(c.authors + ".");
    if (c.doc_title) parts.push(c.doc_title + ".");
    else if (c.content)
      parts.push(
        `"${c.content.slice(0, 160)}${c.content.length > 160 ? "..." : ""}"`,
      );
    if (isPmc) parts.push("PubMed Central.");
    else parts.push(`${isMedRxiv ? "medRxiv" : "bioRxiv"} [Preprint].`);
    if (c.month_year) parts.push(c.month_year.replace(/_/g, " ") + ".");
    if (c.doi) parts.push(`https://doi.org/${c.doi}`);
    else {
      const url = buildUrl(c);
      if (url) parts.push(url);
    }
    return parts.join(" ");
  }

  if (c.doc_title) {
    const parts: string[] = [c.doc_title + "."];
    const url = buildUrl(c);
    if (url) parts.push(url);
    return parts.join(" ");
  }

  return buildUrl(c) || c.doc_id || "";
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const deduplicateCitations = (citations: Citation[]): Citation[] => {
  const seen = new Set<string>();
  return citations.filter((c) => {
    const key = c.doi || c.doc_id || `block_${c.block_id}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const generateExport = (
  citations: Citation[],
  format: ExportFormat,
): string => {
  const unique = deduplicateCitations(citations);

  switch (format) {
    case "ris":
      return unique.map(citationToRIS).join("\r\n\r\n");
    case "bibtex":
      return unique.map(citationToBibTeX).join("\n\n");
    case "txt":
      return unique
        .map((c, i) => {
          const ref = formatPlainTextRef(c);
          return ref ? `[${i + 1}] ${ref}` : null;
        })
        .filter(Boolean)
        .join("\n");
  }
};

const MIME_TYPES: Record<ExportFormat, string> = {
  ris: "application/x-research-info-systems",
  bibtex: "application/x-bibtex",
  txt: "text/plain",
};

const EXTENSIONS: Record<ExportFormat, string> = {
  ris: "ris",
  bibtex: "bib",
  txt: "txt",
};

export const downloadCitations = (
  citations: Citation[],
  format: ExportFormat,
): void => {
  const content = generateExport(citations, format);
  if (!content) return;

  const blob = new Blob([content], { type: MIME_TYPES[format] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `references.${EXTENSIONS[format]}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
